import { useEffect, useRef, useState } from 'react'
import { countUpSegments, pointTier, type PointTier } from '@/lib/ai-result-tier'

type Options = {
  /** false の間は 0 のまま。演出の開始タイミングを外から制御する。 */
  enabled?: boolean
  /** true ならアニメーションせず即座に target と最終段階を返す（演出スキップ用）。 */
  instant?: boolean
  /** 段階の境目を越えた瞬間に呼ぶ（音・光の輪など）。スキップ時は呼ばない。 */
  onTierUp?: (tier: PointTier) => void
  /** 数え終わった瞬間に呼ぶ（ドラムロールの締めなど）。スキップ時は呼ばない。 */
  onComplete?: () => void
}

/**
 * 段階の境目（ai-result-tier の POINT_TIERS）ごとに区切って 0 → target をカウントアップする。
 * 各区間は ease-out で境目の手前で減速し、越えた時点で tier が上がる。
 */
export function useTieredCountUp(
  target: number,
  { enabled = true, instant = false, onTierUp, onComplete }: Options = {},
) {
  const [value, setValue] = useState(0)
  const [tier, setTier] = useState<PointTier>('normal')
  // 親の再描画ごとに関数が変わってもカウントアップをやり直さないよう ref で持つ
  const onTierUpRef = useRef(onTierUp)
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onTierUpRef.current = onTierUp
    onCompleteRef.current = onComplete
  })

  useEffect(() => {
    // 無効化・スキップ時は表示を確定させるのが目的の同期更新（演出のリセット／即時完了）
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!enabled) { setValue(0); setTier('normal'); return }
    if (instant) { setValue(target); setTier(pointTier(target)); return }
    /* eslint-enable react-hooks/set-state-in-effect */
    const segments = countUpSegments(target)
    let index = 0
    let from = 0
    let current: PointTier = 'normal'
    let segmentStart = performance.now()
    let raf: number
    const tick = (now: number) => {
      const seg = segments[index]
      const t = Math.min((now - segmentStart) / seg.durationMs, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(Math.round(from + (seg.to - from) * eased))
      if (t < 1) {
        raf = requestAnimationFrame(tick)
        return
      }
      if (seg.tier !== current) {
        current = seg.tier
        setTier(current)
        onTierUpRef.current?.(current)
      }
      from = seg.to
      index += 1
      segmentStart = now
      if (index < segments.length) {
        raf = requestAnimationFrame(tick)
      } else {
        onCompleteRef.current?.()
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, enabled, instant])

  return { value, tier }
}

export default useTieredCountUp
